import { Injectable } from '@angular/core';
import { CapacitorHttp } from '@capacitor/core';
import { from, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ClientData } from '../interfaces/vehicle-courtesy-use.interface';

/**
 * Cliente tal como viene del JSON cacheado en Azure Blob (InternalFiles/clients.json),
 * generado por el cron del back (ScheduledTaskService.regenerateClientsFile).
 */
interface ClientFileEntry {
  idClient: number;
  country: string;
  idClientOriginal: number | null;
  identityCard: string;
  clientName: string;
  addressStreet: string;
  addressNumber: string;
  addressZipcode: string;
  addressLocation: string;
  addressRegion: string;
  phone: string;
  mail: string;
  dischargeDate: string;
  modifiedDate: string;
  group: string;
  segment: string;
  industry: string;
  application: string;
  fleetT: number;
  fleetS: number;
  fleetG: number;
}

interface ClientsFile {
  generatedAt?: string;
  version?: string;
  total?: number;
  Clients: ClientFileEntry[];
}

/**
 * Servicio de clientes para autocomplete del módulo de cesión de cortesía.
 *
 * Descarga el JSON de clientes desde Azure Blob (InternalFiles/clients.json),
 * generado periódicamente por el back desde la API de Vehículos de Scania.
 *
 * Mantiene el contrato `search(query)` / `getById(idClient)` que ya consume
 * `ClientDataComponent` (sustituye a `ClientsMockService`).
 */
@Injectable({ providedIn: 'root' })
export class ClientsService {

  /** Cache en memoria de la lista (todos los clientes) ya mapeados. */
  private clients$: Observable<ClientData[]> | null = null;

  /** Carga (con caché) y mapea el JSON del blob a `ClientData[]`. */
  private loadClients(): Observable<ClientData[]> {
    if (this.clients$) {
      return this.clients$;
    }

    // El fichero vive en un contenedor privado de Blob Storage, asi que se
    // pide a la API (que si tiene acceso). El endpoint requiere sesion: sin
    // token todavia no se puede cargar y se reintenta mas adelante.
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return of([] as ClientData[]);
    }

    this.clients$ = from(
      CapacitorHttp.get({
        url: `${environment.apiUrl}clients`,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
    ).pipe(
      map(response => {
        if (response.status !== 200 || !response.data?.success) {
          console.error('ClientsService: respuesta inesperada', response.status);
          this.clients$ = null; // permite reintentar en la siguiente busqueda
          return [] as ClientData[];
        }
        // El backend devuelve el contenido del fichero como texto JSON
        const raw = response.data.response;
        const file = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ClientsFile;
        const list = Array.isArray(file?.Clients) ? file.Clients : [];
        return list.map(o => this.toClientData(o));
      }),
      shareReplay(1)
    );

    return this.clients$;
  }

  /**
   * Mapea un cliente del JSON externo al modelo `ClientData` que usa el formulario.
   * Por defecto se asume tipo `10` (Empresa), salvo que el documento parezca un
   * NIF de persona física (8 dígitos + letra).
   */
  private toClientData(o: ClientFileEntry): ClientData {
    const cif = (o.identityCard || '').trim();
    const isPerson = /^\d{8}[A-Z]$/i.test(cif);
    const street = [o.addressStreet, o.addressNumber].filter(Boolean).join(' ').trim();

    return {
      idClient: o.idClient ?? null,
      clientType: isPerson ? 20 : 10,
      clientName: o.clientName || '',
      clientCIF: cif,
      clientCountry: o.country || '',
      clientProvince: o.addressRegion || '',
      clientAddress: street || (o.addressLocation || ''),
      clientPostalCode: o.addressZipcode || '',
      clientEmail: o.mail || '',
      clientMobile: o.phone || ''
    };
  }

  /**
   * Busca clientes por nombre o CIF (case-insensitive, parcial).
   * Devuelve máximo 10 resultados.
   */
  search(query: string): Observable<ClientData[]> {
    const q = (query || '').toLowerCase().trim();
    if (q.length < 2) {
      return of([]);
    }
    return this.loadClients().pipe(
      map(list => list
        .filter(c =>
          c.clientName.toLowerCase().includes(q) ||
          c.clientCIF.toLowerCase().includes(q))
        .slice(0, 10))
    );
  }

  /**
   * Inicia la descarga del JSON en background para que esté en caché
   * cuando el usuario llegue al buscador. Llamar al arranque de la app.
   */
  preload(): void {
    this.loadClients().subscribe({
      next: list => console.log(`[ClientsService] preload: ${list.length} clientes cargados`),
      error: err => console.warn('[ClientsService] preload error:', err)
    });
  }

  getById(idClient: number): Observable<ClientData | null> {
    return this.loadClients().pipe(
      map(list => list.find(c => c.idClient === idClient) ?? null)
    );
  }

  /** Fuerza la próxima carga a recargar el JSON (p.ej. tras pull-to-refresh). */
  invalidateCache(): void {
    this.clients$ = null;
  }
}
